import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Send, Loader2 } from "lucide-react";
import { API, WHATSAPP_URL, PHONE_DISPLAY, PHONE_TEL, EMAIL, LOCATION } from "./constants";

const INFO = [
  { icon: Phone, label: "Telefone", value: PHONE_DISPLAY, href: `tel:${PHONE_TEL}`, testid: "contact-info-phone" },
  { icon: Mail, label: "E-mail", value: EMAIL, href: `mailto:${EMAIL}`, testid: "contact-info-email" },
  { icon: MapPin, label: "Localização", value: LOCATION, href: null, testid: "contact-info-location" },
];

const inputCls =
  "w-full rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-all focus:border-dm-glow/60 focus:bg-white/[0.06] focus:ring-2 focus:ring-dm-glow/15";

const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400";

export default function Contact() {
  const [form, setForm] = useState({ nome: "", email: "", telefone: "", mensagem: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form, { timeout: 15000 });
      toast.success("Mensagem enviada!", {
        description: "Nossa equipe entrará em contato em breve.",
      });
      setForm({ nome: "", email: "", telefone: "", mensagem: "" });
    } catch {
      toast.error("Não foi possível enviar.", {
        description: "Tente novamente ou fale conosco pelo WhatsApp.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contato" className="relative py-24 lg:py-32 carto-grid">
      <div className="mx-auto max-w-7xl px-5 lg:px-8 grid gap-14 lg:grid-cols-2 lg:gap-20 items-start">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.6 }}
            className="font-mono text-xs uppercase tracking-[0.32em] text-dm-contour"
          >
            Contato
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ delay: 0.1, duration: 0.7 }}
            className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white"
          >
            Informações de <span className="text-gradient">Contato</span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="mt-10 space-y-4"
          >
            {INFO.map((info) => {
              const Wrapper = info.href ? "a" : "div";
              return (
                <Wrapper
                  key={info.label}
                  data-testid={info.testid}
                  href={info.href || undefined}
                  className="flex items-center gap-4 rounded-xl glass px-5 py-4 transition-colors duration-300 hover:border-dm-glow/35"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-dm-blue/20 border border-dm-glow/25 text-dm-glow">
                    <info.icon size={18} />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                      {info.label}
                    </span>
                    <span className="block mt-0.5 font-medium text-slate-100">{info.value}</span>
                  </span>
                </Wrapper>
              );
            })}
          </motion.div>

          <motion.a
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            data-testid="contact-whatsapp-button"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-3 rounded-full bg-[#25D366] hover:bg-[#1fb857] text-[#04250f] font-semibold px-7 py-4 transition-all hover:-translate-y-0.5 hover:shadow-[0_0_36px_rgba(37,211,102,0.4)]"
          >
            <MessageCircle size={19} />
            Orçamento pelo WhatsApp — {PHONE_DISPLAY}
          </motion.a>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-2xl glass p-7 lg:p-10 shadow-[0_8px_32px_rgba(0,10,30,0.6)]"
        >
          <h3 className="font-display text-xl lg:text-2xl font-semibold text-white">
            Mande uma mensagem
          </h3>
          <p className="mt-2 text-sm text-slate-400">
            Preencha os campos e nossa equipe responderá em breve.
          </p>

          <form data-testid="contact-form" onSubmit={onSubmit} className="mt-8 space-y-5">
            <div>
              <label htmlFor="nome" className={labelCls}>Nome</label>
              <input
                id="nome"
                data-testid="contact-name-input"
                className={inputCls}
                placeholder="Seu Nome"
                value={form.nome}
                onChange={set("nome")}
                required
                minLength={2}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="email" className={labelCls}>E-mail</label>
                <input
                  id="email"
                  data-testid="contact-email-input"
                  type="email"
                  className={inputCls}
                  placeholder="Seu E-mail"
                  value={form.email}
                  onChange={set("email")}
                  required
                />
              </div>
              <div>
                <label htmlFor="telefone" className={labelCls}>Telefone</label>
                <input
                  id="telefone"
                  data-testid="contact-phone-input"
                  type="tel"
                  className={inputCls}
                  placeholder="Seu Telefone"
                  value={form.telefone}
                  onChange={set("telefone")}
                  required
                />
              </div>
            </div>
            <div>
              <label htmlFor="mensagem" className={labelCls}>Mensagem</label>
              <textarea
                id="mensagem"
                data-testid="contact-message-input"
                rows={4}
                className={`${inputCls} resize-none`}
                placeholder="Como podemos ajudar no seu projeto?"
                value={form.mensagem}
                onChange={set("mensagem")}
                required
              />
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="w-full rounded-xl bg-dm-blue hover:bg-[#0052d9] disabled:opacity-60 text-white font-semibold py-4 flex items-center justify-center gap-2.5 shadow-[0_0_24px_rgba(0,98,255,0.45)] transition-all hover:shadow-[0_0_40px_rgba(0,98,255,0.7)]"
            >
              {sending ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send size={17} />
                  Enviar Mensagem
                </>
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
